import { hashQueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlideCaseAssignmentOptionsRequest } from 'api/slides';
import { useEffect, useState } from 'react';
import { loadSlideCaseAssignmentOptions } from './loadSlideCaseAssignmentOptions';

interface SlideCaseAssignmentOptionsQueryOptions {
  request: Omit<SlideCaseAssignmentOptionsRequest, 'cursor'>;
  callerId: string;
  generationId: string;
  enabled: boolean;
}

export const useSlideCaseAssignmentOptions = ({
  request,
  callerId,
  generationId,
  enabled,
}: SlideCaseAssignmentOptionsQueryOptions) => {
  const queryClient = useQueryClient();
  const queryKey = [
    'slideCaseAssignmentOptions',
    callerId,
    generationId,
    { includeAssigned: false, pageSize: 100, ...request },
  ] as const;
  const queryIdentity = hashQueryKey(queryKey);
  const [progress, setProgress] = useState({ identity: queryIdentity, loadedCount: 0 });
  const canLoad = enabled && Boolean(callerId && generationId && request.labId && request.targetStudyId);
  const query = useQuery(
    queryKey,
    ({ signal }) => {
      setProgress({ identity: queryIdentity, loadedCount: 0 });
      return loadSlideCaseAssignmentOptions(request, signal, (loadedCount) =>
        setProgress({ identity: queryIdentity, loadedCount })
      );
    },
    {
      enabled: canLoad,
      retry: false,
      cacheTime: 0,
      staleTime: 0,
      keepPreviousData: false,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    return () => {
      void queryClient.cancelQueries({ queryKey, exact: true });
    };
  }, [queryClient, queryIdentity, canLoad]);

  const isComplete = canLoad && query.isSuccess && !query.isFetching;

  return {
    items: isComplete ? query.data : undefined,
    isComplete,
    isLoading: canLoad && (query.isLoading || query.isFetching),
    loadedCount: canLoad && progress.identity === queryIdentity ? progress.loadedCount : 0,
    error: canLoad ? query.error : null,
    refetch: query.refetch,
  };
};
